import type { MigrationAttemptRecord } from "./analytics";
import type { PendingMigrationSwap } from "./swapRecovery";

/** Only confirmed migration proceeds are spendable; pending swaps always block a new signature. */
export interface IntermediateUsdc {
    amount: string;
    analytics?: MigrationAttemptRecord;
    pendingSwap?: PendingMigrationSwap;
    /** Compatibility with records created before durable submission tracking. */
    unresolvedSignature?: string;
}

const key = (owner: string): string => `solomon:usdv-migration:usdc:${owner}`;

function isPendingSwap(value: unknown): value is PendingMigrationSwap {
    if (!value || typeof value !== "object") return false;
    const record = value as Record<string, unknown>;
    return (
        (record.leg === "legacy-to-usdc" || record.leg === "usdc-to-usdv") &&
        typeof record.signature === "string" &&
        record.signature.length > 0 &&
        typeof record.recentBlockhash === "string" &&
        record.recentBlockhash.length > 0 &&
        (record.searchFromSlot === undefined ||
            (typeof record.searchFromSlot === "number" &&
                Number.isSafeInteger(record.searchFromSlot) &&
                record.searchFromSlot >= 0)) &&
        typeof record.inputAmount === "string" &&
        /^\d+$/.test(record.inputAmount) &&
        BigInt(record.inputAmount) > 0n &&
        typeof record.remainingLegacyUsdv === "string" &&
        /^\d+(\.\d{1,9})?$/.test(record.remainingLegacyUsdv)
    );
}

export function readIntermediateUsdc(owner: string | null): IntermediateUsdc {
    if (!owner) return { amount: "0" };
    const raw = sessionStorage.getItem(key(owner));
    if (!raw) return { amount: "0" };
    const value: unknown = JSON.parse(raw);
    if (
        !value ||
        typeof value !== "object" ||
        !("amount" in value) ||
        typeof value.amount !== "string" ||
        !/^\d+(\.\d{1,6})?$/.test(value.amount) ||
        ("unresolvedSignature" in value && typeof value.unresolvedSignature !== "string") ||
        ("pendingSwap" in value && !isPendingSwap(value.pendingSwap))
    ) {
        throw new Error("Could not read the pending migration USDC amount.");
    }
    return {
        amount: value.amount,
        ...("analytics" in value && isAttemptRecord(value.analytics) ? { analytics: value.analytics } : {}),
        ...("pendingSwap" in value
            ? { pendingSwap: cleanPendingAnalytics(value.pendingSwap as PendingMigrationSwap) }
            : {}),
        ...("unresolvedSignature" in value ? { unresolvedSignature: value.unresolvedSignature as string } : {})
    };
}

export function saveIntermediateUsdc(owner: string, value: IntermediateUsdc): void {
    sessionStorage.setItem(key(owner), JSON.stringify(value));
}

function cleanPendingAnalytics(pending: PendingMigrationSwap): PendingMigrationSwap {
    const analytics = pending.analytics;
    if (!analytics) return pending;
    if (isAttemptRecord(analytics)) return pending;
    const { analytics: _ignored, ...rest } = pending;
    return rest;
}

function isAttemptRecord(value: unknown): value is MigrationAttemptRecord {
    if (!value || typeof value !== "object") return false;
    const record = value as Record<string, unknown>;
    return (
        typeof record.journeyId === "string" &&
        typeof record.attemptId === "string" &&
        typeof record.number === "number" &&
        Number.isSafeInteger(record.number) &&
        record.number > 0 &&
        typeof record.step === "string" &&
        ["unstake_legacy_susdv", "claim_legacy_usdv", "swap_legacy_usdv_to_usdc", "swap_usdc_to_usdv"].includes(
            record.step
        )
    );
}
